import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import type {ReactNode} from 'react'
import {useLayoutEffect} from 'react'
import type {EntryPointProps} from 'react-relay'

import BuildLineLayout from '../../../containers/BuildLineLayout'
import BuildsFetcher from '../../../containers/BuildsFetcher'
import BuildsWithLastReadyLocator from '../../../containers/BuildsWithLastReadyLocator'
import {OneLineDetector} from '../../../containers/OneLineDetector'
import {getBuildTypeHasNonDefaultBranchesArg} from '../../../rest/branches'
import type {Branch} from '../../../services/rest'
import {restApi} from '../../../services/rest'
import type {BuildTypeId} from '../../../types'
import {stringifyBranch} from '../../../utils/branchNames'
import type {SimpleStream} from '../../../utils/simpleStream'
import {useBuildsClassName} from '../../common/Builds/Builds'
import BuildsContainer from '../../common/Builds/Builds.container'
import BuildsFilters from '../../common/BuildsFilters/BuildsFilters.container'
import Grid from '../../common/Grid/Grid'
import RowGroup from '../../common/Grid/RowGroup'
import {submitPager} from '../../common/Pager/Pager.actions.base'
import {usePage} from '../../common/Pager/Pager.hooks'
import type {PermalinksQuery} from '../../common/Permalinks/__generated__/PermalinksQuery.graphql'

import BuildTypeOverviewFooter from './BuildTypeOverviewFooter/BuildTypeOverviewFooter.container'

import styles from './BuildTypeOverview.css'

import {useAppDispatch} from 'src/hooks/react-redux'

type BuildsGridProps = {
  children: ReactNode
}
function BuildsGrid({children}: BuildsGridProps) {
  const className = useBuildsClassName()
  return <Grid className={className}>{children}</Grid>
}

export type Props = {
  branch?: Branch
  buildTypeId: BuildTypeId
  maxBuildCount: number
  showAgent: boolean
  update$?: SimpleStream<void>
  hasRunningBuilds?: boolean
  hasFinishedBuilds?: boolean
}

type Queries = {
  permalinks: PermalinksQuery
}

function BuildTypeOverview({queries, props}: EntryPointProps<Queries, {}, Props, {}>) {
  const {
    buildTypeId,
    branch,
    maxBuildCount,
    showAgent,
    update$,
    hasRunningBuilds = false,
    hasFinishedBuilds = true,
  } = props
  const dispatch = useAppDispatch()
  const {hasNonDefaultBranches} = restApi.endpoints.checkIfBranchExistsInBuildType.useQuery(
    getBuildTypeHasNonDefaultBranchesArg(buildTypeId),
    {selectFromResult: ({data}) => ({hasNonDefaultBranches: data})},
  )
  const hasBranches = hasNonDefaultBranches || (branch != null && !branch.default)
  useLayoutEffect(() => {
    dispatch(
      submitPager({
        pageSize: maxBuildCount,
        precountedPages: 1,
      }),
    )
  }, [dispatch, maxBuildCount])
  const page = usePage()
  return (
    <BuildLineLayout showAgent={showAgent} showPath={false} showBranch={hasBranches}>
      <OneLineDetector>
        <BuildsGrid>
          {hasRunningBuilds && (
            <div className={styles.container} data-running-builds>
              <BuildsFetcher
                buildTypeId={buildTypeId}
                branch={branch}
                withRunning
                buildState="running"
              >
                <BuildsContainer
                  placeholder={null}
                  buildTypeId={buildTypeId}
                  branch={branch}
                  withLoader={false}
                  alwaysShowLineBorders
                  hasParentGrid
                />
              </BuildsFetcher>
            </div>
          )}
          {hasFinishedBuilds && (
            <div className={styles.container} data-short-history>
              <BuildsFetcher
                buildTypeId={buildTypeId}
                branch={branch}
                updatePeriod={null}
                update$={update$}
                withPager
              >
                <RowGroup>
                  <div className={styles.row}>
                    <H3 className={styles.title}>{'Recent history'}</H3>
                    <BuildsFilters
                      disableAdvancedMode
                      withStateFilter
                      withTagFilter
                      buildTypeId={buildTypeId}
                    />
                  </div>
                </RowGroup>
                <BuildsWithLastReadyLocator
                  placeholder="No builds found"
                  alwaysShowLineBorders
                  showOverlayOnLoading
                  withTitle
                  withLoader
                  hasParentGrid
                  withPager
                />
                <RowGroup>
                  <div className={styles.row}>
                    <BuildTypeOverviewFooter
                      maxBuildCount={maxBuildCount}
                      branch={stringifyBranch(branch)}
                      buildTypeId={buildTypeId}
                      page={page}
                      permalinksQuery={queries.permalinks}
                    />
                  </div>
                </RowGroup>
              </BuildsFetcher>
            </div>
          )}
        </BuildsGrid>
      </OneLineDetector>
    </BuildLineLayout>
  )
}

export type BuildTypeOverviewComponent = typeof BuildTypeOverview

export default BuildTypeOverview
